/**
 * Espace partenaire — Mes leads. Le partenaire transmet des fiches client à
 * BCL et suit leur prise en charge.
 */
import type { LeadNeedType } from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { extractErrorMessage } from '@/features/auth/error-message';
import { createLead, listMyLeads } from '@/features/partners/leads-api';
import { leadStatusBadge, needTypeLabels } from '@/features/partners/lead-labels';

const needTypeOptions = Object.entries(needTypeLabels) as [LeadNeedType, string][];

const emptyForm = {
  clientFirstName: '',
  clientLastName: '',
  clientPhone: '',
  clientEmail: '',
  addressLine: '',
  postalCode: '',
  city: '',
  needType: 'etude_raccordement' as LeadNeedType,
  comment: '',
};

export function PartnerLeadsPage() {
  const qc = useQueryClient();
  const leads = useQuery({ queryKey: ['partner', 'leads'], queryFn: listMyLeads });
  const [form, setForm] = useState(emptyForm);

  const createMut = useMutation({
    mutationFn: () =>
      createLead({
        clientFirstName: form.clientFirstName,
        clientLastName: form.clientLastName,
        clientPhone: form.clientPhone,
        clientEmail: form.clientEmail,
        addressLine: form.addressLine,
        postalCode: form.postalCode,
        city: form.city,
        needType: form.needType,
        ...(form.comment ? { comment: form.comment } : {}),
      }),
    onSuccess: () => {
      toast.success('Lead transmis à BCL');
      qc.invalidateQueries({ queryKey: ['partner', 'leads'] });
      setForm(emptyForm);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const valid =
    form.clientFirstName &&
    form.clientLastName &&
    form.clientPhone &&
    form.clientEmail &&
    form.addressLine &&
    form.postalCode &&
    form.city;

  return (
    <DashboardLayout variant="partenaire" title="Mes leads">
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Leads transmis</CardTitle>
            <CardDescription>
              {leads.data ? `${leads.data.length} lead(s)` : 'Chargement…'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Besoin</TableHead>
                  <TableHead>Ville</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.data?.length ? (
                  leads.data.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell className="font-medium">
                        {lead.clientFirstName} {lead.clientLastName}
                      </TableCell>
                      <TableCell className="text-sm">
                        {needTypeLabels[lead.needType]}
                      </TableCell>
                      <TableCell className="text-sm">{lead.city}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={leadStatusBadge[lead.status].cls}>
                          {leadStatusBadge[lead.status].label}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-gray-500">
                      Aucun lead transmis.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Nouveau lead
            </CardTitle>
            <CardDescription>Transmettez une fiche client à BCL.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="clientFirstName">Prénom</Label>
                <Input
                  id="clientFirstName"
                  value={form.clientFirstName}
                  onChange={(e) => setForm({ ...form, clientFirstName: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="clientLastName">Nom</Label>
                <Input
                  id="clientLastName"
                  value={form.clientLastName}
                  onChange={(e) => setForm({ ...form, clientLastName: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="clientEmail">Email</Label>
              <Input
                id="clientEmail"
                type="email"
                value={form.clientEmail}
                onChange={(e) => setForm({ ...form, clientEmail: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="clientPhone">Téléphone</Label>
              <Input
                id="clientPhone"
                value={form.clientPhone}
                onChange={(e) => setForm({ ...form, clientPhone: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="addressLine">Adresse</Label>
              <Input
                id="addressLine"
                value={form.addressLine}
                onChange={(e) => setForm({ ...form, addressLine: e.target.value })}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="postalCode">Code postal</Label>
                <Input
                  id="postalCode"
                  value={form.postalCode}
                  onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="city">Ville</Label>
                <Input
                  id="city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="needType">Besoin</Label>
              <select
                id="needType"
                value={form.needType}
                onChange={(e) =>
                  setForm({ ...form, needType: e.target.value as LeadNeedType })
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {needTypeOptions.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="comment">Commentaire</Label>
              <Input
                id="comment"
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
              />
            </div>
            <Button
              className="w-full"
              disabled={!valid || createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              {createMut.isPending ? 'Envoi…' : 'Transmettre à BCL'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
